/**
 * DataGridDiff - Visual cell-by-cell diff for algae/zooplankton data grids.
 * Renders a table where changed cells are highlighted.
 */

const COLOR_MAP = {
  green: 'bg-emerald-100',
  yellow: 'bg-yellow-100',
  orange: 'bg-orange-100',
  red: 'bg-red-100',
};

function getShortName(fullName) {
  const m = fullName.match(/- (.+)$/);
  return m ? m[1] : fullName;
}

function formatValue(val) {
  if (!val || val === '' || val === '0') return '–';
  const num = parseInt(String(val).replace(/[<>\s]/g, ''), 10);
  if (String(val).startsWith('<')) return `<${num.toLocaleString('nb-NO')}`;
  if (!isNaN(num)) return num.toLocaleString('nb-NO');
  return val;
}

export default function DataGridDiff({ data, gridDiff, colorOverrides, tempData, title, unit }) {
  if (!data || Object.keys(data).length === 0) return null;

  const firstOrg = Object.values(data)[0] || {};
  const locations = Object.keys(firstOrg);
  const changedCellCount = gridDiff ? Object.keys(gridDiff).length : 0;

  return (
    <div className="mb-5" data-testid={`grid-diff-${title}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
        {changedCellCount > 0 && (
          <span className="text-[10px] font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="diff-change-count">
            {changedCellCount} celle{changedCellCount !== 1 ? 'r' : ''} endra
          </span>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-2.5 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 min-w-[110px]">Orden/gruppe</th>
              <th className="px-2.5 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider sticky bg-slate-50 min-w-[110px]" style={{ left: '110px' }}>Art</th>
              {locations.map(loc => (
                <th key={loc} className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500 bg-slate-50 min-w-[70px]">
                  <div>{getShortName(loc)}</div>
                  <div className="text-[9px] text-slate-400 font-normal">({unit})</div>
                  {tempData?.[loc] && (
                    <div className="mt-0.5 text-[9px] font-semibold text-sky-600 bg-sky-50 rounded-full px-1 py-0.5 inline-block">
                      {tempData[loc]}°C
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([orgKey, locData], i) => {
              const parts = orgKey.split('_');
              const group = parts[0] || '';
              const species = parts.slice(1).join('_') || orgKey;
              const isSUM = species.toUpperCase().includes('SUM');

              return (
                <tr key={orgKey} className={`border-b border-slate-100 ${isSUM ? 'bg-slate-50 font-semibold' : ''}`}>
                  <td className={`px-2.5 py-1.5 text-slate-600 sticky left-0 ${isSUM ? 'bg-slate-50' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                    {group}
                  </td>
                  <td className={`px-2.5 py-1.5 text-slate-700 italic sticky ${isSUM ? 'bg-slate-50 not-italic font-bold' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} style={{ left: '110px' }}>
                    {species}
                  </td>
                  {locations.map(loc => {
                    const val = locData?.[loc] || '';
                    const diffKey = `${orgKey}||${loc}`;
                    const cellDiff = gridDiff?.[diffKey];
                    const overrideKey = `${orgKey}_${loc}`;
                    const colorClass = colorOverrides?.[overrideKey] ? COLOR_MAP[colorOverrides[overrideKey]] || '' : '';

                    if (cellDiff) {
                      return (
                        <td key={loc} className="px-1 py-1 text-center relative group" data-testid={`diff-cell-${orgKey}-${loc}`}>
                          <div className="bg-amber-50 border border-amber-300 rounded px-1 py-0.5">
                            <div className="text-[10px] text-red-600 line-through opacity-70">{formatValue(cellDiff.old) || '–'}</div>
                            <div className="text-[11px] text-emerald-700 font-semibold">{formatValue(cellDiff.new) || '–'}</div>
                          </div>
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 pointer-events-none">
                            <div className="bg-slate-900 text-white text-[10px] rounded-md px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                              <span className="text-red-300">{cellDiff.old || '(tom)'}</span>
                              <span className="mx-1 text-slate-400">&rarr;</span>
                              <span className="text-emerald-300">{cellDiff.new || '(tom)'}</span>
                            </div>
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td key={loc} className={`px-1 py-1.5 text-right tabular-nums ${isSUM ? 'font-bold' : ''} ${colorClass || (isSUM ? 'bg-teal-50' : '')}`}>
                        {formatValue(val)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
